'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import Modal from '@/components/ui/Modal';
import { useTheme } from '@/context/ThemeContext';
import { createClient } from '@/lib/supabase/client';

interface Proposal {
  id: string;
  code: string;
  title: string;
  customer: string;
  value: string;
  stage: string;
  stageIndex: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
  completion: number;
  deadline: string;
  hoursLeft: number;
  operators: string[];
  blocker: string | null;
  category: 'active' | 'review' | 'closing' | 'won' | 'lost';
}

const KANBAN_COLS = [
  { id: 'active', label: 'Active', color: '#1E90FF', icon: 'BoltIcon' },
  { id: 'review', label: 'In Review', color: '#F59E0B', icon: 'ClipboardDocumentCheckIcon' },
  { id: 'closing', label: 'Closing', color: '#00D68F', icon: 'FlagIcon' },
  { id: 'won', label: 'Won', color: '#00D68F', icon: 'TrophyIcon' },
  { id: 'lost', label: 'Lost', color: '#FF4757', icon: 'XCircleIcon' },
];

const PRIORITY_COLORS: Record<string, string> = {
  critical: '#FF4757',
  high: '#F59E0B',
  medium: '#1E90FF',
  low: '#6B7FA3',
};

const QUICK_FILTERS = ['All', 'My Proposals', 'Needs Action', 'Expiring Soon', 'Won', 'Lost'];

function formatDeadline(deadlineAt: string | null): { label: string; hoursLeft: number } {
  if (!deadlineAt) return { label: '—', hoursLeft: 0 };
  const diff = new Date(deadlineAt).getTime() - Date.now();
  if (diff <= 0) return { label: 'Overdue', hoursLeft: 0 };
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  if (days > 0) return { label: remHours > 0 ? `${days}d ${remHours}h` : `${days}d`, hoursLeft: hours };
  return { label: `${hours}h`, hoursLeft: hours };
}

const EMPTY_FORM = {
  title: '',
  customer_name: '',
  value_amount: '',
  value_currency: 'OMR',
  priority: 'medium',
  deadline_at: '',
  owner_name: '',
  category: 'active',
  stage: 'Initiated',
  requirement_text: '',
};

export default function ProposalsPage() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [view, setView] = useState<'kanban' | 'table'>('kanban');
  const [activeFilter, setActiveFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const fetchProposals = async () => {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('id, code, title, customer_name, value_amount, value_currency, stage, stage_index, priority, completion, deadline_at, owner_name, category, blocker')
        .order('created_at', { ascending: false });

      if (error) { console.error('Error fetching proposals:', error.message); return; }

      const mapped: Proposal[] = (data || []).map((p: any) => {
        const { label, hoursLeft } = formatDeadline(p.deadline_at);
        return {
          id: p.id,
          code: p.code,
          title: p.title,
          customer: p.customer_name,
          value: `${Number(p.value_amount).toLocaleString()} ${p.value_currency}`,
          stage: p.stage,
          stageIndex: p.stage_index,
          priority: p.priority,
          completion: p.completion,
          deadline: label,
          hoursLeft,
          operators: p.owner_name ? [p.owner_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)] : [],
          blocker: p.blocker,
          category: p.category,
        };
      });
      setProposals(mapped);
    } catch (err) {
      console.error('Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, []);

  const handleCreateProposal = async () => {
    if (!formData.title.trim() || !formData.customer_name.trim()) {
      setSaveError('Title and Customer are required.');
      return;
    }
    setSaving(true);
    setSaveError('');
    const supabase = createClient();
    try {
      const count = proposals.length + 1;
      const code = `PRP-${String(count).padStart(4, '0')}`;
      const { error } = await supabase.from('proposals').insert({
        code,
        title: formData.title,
        customer_name: formData.customer_name,
        value_amount: Number(formData.value_amount) || 0,
        value_currency: formData.value_currency,
        priority: formData.priority,
        deadline_at: formData.deadline_at || null,
        owner_name: formData.owner_name || null,
        category: formData.category,
        stage: formData.stage,
        stage_index: 0,
        completion: 0,
        requirement_text: formData.requirement_text || null,
      });
      if (error) { setSaveError(error.message); return; }
      setShowNewModal(false);
      setFormData(EMPTY_FORM);
      await fetchProposals();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to create proposal');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProposal = async (id: string) => {
    if (!confirm('Delete this proposal? This cannot be undone.')) return;
    const supabase = createClient();
    await supabase.from('proposals').delete().eq('id', id);
    setProposals(prev => prev.filter(p => p.id !== id));
    setOpenMenuId(null);
  };

  const handleMarkWon = async (id: string) => {
    const supabase = createClient();
    await supabase.from('proposals').update({ category: 'won', stage: 'Won / Lost' }).eq('id', id);
    setProposals(prev => prev.map(p => p.id === id ? { ...p, category: 'won', stage: 'Won / Lost' } : p));
    setOpenMenuId(null);
  };

  const handleMarkLost = async (id: string) => {
    const supabase = createClient();
    await supabase.from('proposals').update({ category: 'lost', stage: 'Won / Lost' }).eq('id', id);
    setProposals(prev => prev.map(p => p.id === id ? { ...p, category: 'lost', stage: 'Won / Lost' } : p));
    setOpenMenuId(null);
  };

  const filtered = proposals.filter((p) => {
    if (search && !p.title.toLowerCase().includes(search.toLowerCase()) && !p.code.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeFilter === 'Needs Action') return p.blocker !== null;
    if (activeFilter === 'Expiring Soon') return p.hoursLeft > 0 && p.hoursLeft < 120;
    if (activeFilter === 'Won') return p.category === 'won';
    if (activeFilter === 'Lost') return p.category === 'lost';
    return true;
  });

  const cardBg = isLight ? '#FFFFFF' : '#0B1628';
  const borderColor = isLight ? '#E2E8F0' : 'rgba(30,144,255,0.12)';
  const mutedText = isLight ? '#64748B' : '#6B7FA3';
  const headingText = isLight ? '#0F172A' : '#FFFFFF';
  const inputBg = isLight ? '#F8FAFC' : '#0D1829';

  const inputStyle: React.CSSProperties = {
    background: inputBg,
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
    padding: '8px 12px',
    color: headingText,
    fontSize: 12,
    outline: 'none',
    width: '100%',
  };

  if (loading) {
    return (
      <AppLayout pageTitle="All Proposals">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm" style={{ color: mutedText }}>Loading proposals...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="All Proposals">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-700" style={{ color: headingText }}>All Proposals</h1>
            <p className="text-sm mt-0.5" style={{ color: mutedText }}>{proposals.length} total · {proposals.filter(p => p.blocker).length} need action</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg overflow-hidden" style={{ border: `1px solid ${borderColor}` }}>
              <button onClick={() => setView('kanban')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 transition-all" style={{ background: view === 'kanban' ? 'rgba(30,144,255,0.15)' : 'transparent', color: view === 'kanban' ? '#1E90FF' : mutedText }}>
                <Icon name="ViewColumnsIcon" size={14} />Kanban
              </button>
              <button onClick={() => setView('table')} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 transition-all" style={{ background: view === 'table' ? 'rgba(30,144,255,0.15)' : 'transparent', color: view === 'table' ? '#1E90FF' : mutedText, borderLeft: `1px solid ${borderColor}` }}>
                <Icon name="TableCellsIcon" size={14} />Table
              </button>
            </div>
            <button onClick={() => { setShowNewModal(true); setSaveError(''); setFormData(EMPTY_FORM); }} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5">
              <Icon name="PlusIcon" size={14} />New Proposal
            </button>
          </div>
        </div>

        {/* Filters + Search */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex items-center">
            <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-2.5" style={{ color: mutedText } as React.CSSProperties} />
            <input type="text" placeholder="Search proposals..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 pr-3 py-1.5 text-xs rounded-lg w-52 outline-none" style={{ background: isLight ? '#F1F5F9' : 'rgba(13,24,41,0.8)', border: `1px solid ${borderColor}`, color: headingText }} />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {QUICK_FILTERS.map((f) => (
              <button key={f} onClick={() => setActiveFilter(f)} className="px-3 py-1.5 text-xs font-500 rounded-lg transition-all" style={{ background: activeFilter === f ? 'rgba(30,144,255,0.15)' : isLight ? '#F1F5F9' : 'rgba(13,24,41,0.6)', color: activeFilter === f ? '#1E90FF' : mutedText, border: `1px solid ${activeFilter === f ? 'rgba(30,144,255,0.3)' : borderColor}`, fontWeight: activeFilter === f ? 600 : 500 }}>{f}</button>
            ))}
          </div>
        </div>

        {/* Kanban View */}
        {view === 'kanban' && (
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
            {KANBAN_COLS.map((col) => {
              const colProposals = filtered.filter((p) => p.category === col.id);
              return (
                <div key={col.id} className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 px-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: col.color, boxShadow: `0 0 6px ${col.color}80` }} />
                    <span className="text-xs font-700 uppercase tracking-wider" style={{ color: col.color }}>{col.label}</span>
                    <span className="ml-auto text-xs font-600 px-1.5 py-0.5 rounded-full" style={{ background: `${col.color}20`, color: col.color }}>{colProposals.length}</span>
                  </div>
                  {colProposals.map((p) => (
                    <Link key={p.id} href={`/proposals/${p.id}`}>
                      <div className="rounded-xl p-3 cursor-pointer card-hover" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
                        <div className="h-1 rounded-full mb-3 overflow-hidden" style={{ background: isLight ? '#E2E8F0' : '#1A2D4A' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${p.completion}%`, background: `linear-gradient(90deg, ${col.color}, ${col.color}99)` }} />
                        </div>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-[10px] font-600" style={{ color: '#1E90FF' }}>{p.code}</span>
                          <span className="w-2 h-2 rounded-full shrink-0 mt-0.5" style={{ background: PRIORITY_COLORS[p.priority], boxShadow: `0 0 5px ${PRIORITY_COLORS[p.priority]}80` }} />
                        </div>
                        <p className="text-xs font-600 leading-tight mb-1" style={{ color: headingText }}>{p.title}</p>
                        <p className="text-[11px] mb-2" style={{ color: mutedText }}>{p.customer}</p>
                        {p.blocker && (
                          <div className="flex items-center gap-1 mb-2 px-2 py-1 rounded-md" style={{ background: 'rgba(255,71,87,0.1)', border: '1px solid rgba(255,71,87,0.2)' }}>
                            <Icon name="ExclamationTriangleIcon" size={10} style={{ color: '#FF4757' } as React.CSSProperties} />
                            <span className="text-[10px] font-600" style={{ color: '#FF4757' }}>{p.blocker}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] font-700" style={{ color: headingText }}>{p.value}</span>
                          <div className="flex -space-x-1">
                            {p.operators.slice(0, 3).map((op) => (
                              <div key={op} className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-700" style={{ background: 'linear-gradient(135deg, #1E90FF, #0066CC)', border: `1px solid ${cardBg}` }}>{op}</div>
                            ))}
                          </div>
                        </div>
                        {p.hoursLeft > 0 && p.hoursLeft < 200 && (
                          <div className="mt-2 flex items-center gap-1">
                            <Icon name="ClockIcon" size={10} style={{ color: p.hoursLeft < 72 ? '#FF4757' : '#F59E0B' } as React.CSSProperties} />
                            <span className="text-[10px]" style={{ color: p.hoursLeft < 72 ? '#FF4757' : '#F59E0B' }}>Due {p.deadline}</span>
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                  {colProposals.length === 0 && (
                    <div className="rounded-xl p-4 text-center" style={{ background: isLight ? '#F8FAFC' : 'rgba(11,22,40,0.4)', border: `1px dashed ${borderColor}` }}>
                      <p className="text-xs" style={{ color: mutedText }}>No proposals</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Table View */}
        {view === 'table' && (
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${borderColor}` }}>
            <table className="w-full">
              <thead>
                <tr style={{ background: isLight ? '#F8FAFC' : '#0D1829', borderBottom: `1px solid ${borderColor}` }}>
                  {['Code', 'Title', 'Customer', 'Stage', 'Value', 'Completion', 'Deadline', 'Team', ''].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-700 uppercase tracking-wider" style={{ color: mutedText }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr key={p.id} className="row-hover transition-colors" style={{ borderBottom: i < filtered.length - 1 ? `1px solid ${borderColor}` : 'none' }}>
                    <td className="px-4 py-3"><span className="text-xs font-600" style={{ color: '#1E90FF' }}>{p.code}</span></td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-600" style={{ color: headingText }}>{p.title}</p>
                        {p.blocker && <p className="text-[10px] mt-0.5" style={{ color: '#FF4757' }}>⚠ {p.blocker}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: mutedText }}>{p.customer}</td>
                    <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full font-500" style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF' }}>{p.stage}</span></td>
                    <td className="px-4 py-3 text-sm font-600" style={{ color: headingText }}>{p.value}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: isLight ? '#E2E8F0' : '#1A2D4A', minWidth: 60 }}>
                          <div className="h-full rounded-full" style={{ width: `${p.completion}%`, background: 'linear-gradient(90deg, #1E90FF, #00D68F)' }} />
                        </div>
                        <span className="text-xs font-600" style={{ color: mutedText }}>{p.completion}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: p.hoursLeft > 0 && p.hoursLeft < 72 ? '#FF4757' : mutedText }}>{p.deadline}</td>
                    <td className="px-4 py-3">
                      <div className="flex -space-x-1">
                        {p.operators.slice(0, 3).map((op) => (
                          <div key={op} className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-700" style={{ background: 'linear-gradient(135deg, #1E90FF, #0066CC)', border: `1px solid ${cardBg}` }}>{op}</div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 relative">
                        <Link href={`/proposals/${p.id}`} className="text-xs font-600 px-2.5 py-1.5 rounded-lg transition-all" style={{ background: 'rgba(30,144,255,0.1)', color: '#1E90FF' }}>Open →</Link>
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === p.id ? null : p.id)}
                            className="p-1.5 rounded-lg transition-all"
                            style={{ background: 'rgba(107,127,163,0.1)', color: mutedText }}
                          >
                            <Icon name="EllipsisVerticalIcon" size={14} />
                          </button>
                          {openMenuId === p.id && (
                            <div className="absolute right-0 top-8 z-20 rounded-xl shadow-lg py-1 min-w-[140px]" style={{ background: cardBg, border: `1px solid ${borderColor}` }}>
                              <button onClick={() => handleMarkWon(p.id)} className="w-full text-left px-3 py-2 text-xs font-500 flex items-center gap-2 hover:bg-green-500/10 transition-colors" style={{ color: '#00D68F' }}>
                                <Icon name="TrophyIcon" size={13} />Mark Won
                              </button>
                              <button onClick={() => handleMarkLost(p.id)} className="w-full text-left px-3 py-2 text-xs font-500 flex items-center gap-2 hover:bg-yellow-500/10 transition-colors" style={{ color: '#F59E0B' }}>
                                <Icon name="XCircleIcon" size={13} />Mark Lost
                              </button>
                              <div style={{ borderTop: `1px solid ${borderColor}`, margin: '2px 0' }} />
                              <button onClick={() => handleDeleteProposal(p.id)} className="w-full text-left px-3 py-2 text-xs font-500 flex items-center gap-2 hover:bg-red-500/10 transition-colors" style={{ color: '#FF4757' }}>
                                <Icon name="TrashIcon" size={13} />Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Proposal Modal */}
      <Modal open={showNewModal} onClose={() => setShowNewModal(false)} title="Create New Proposal" size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-600 mb-1.5 block" style={{ color: mutedText }}>Proposal Title *</label>
              <input
                type="text"
                placeholder="e.g. Network Infrastructure Upgrade for ADNOC"
                value={formData.title}
                onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: mutedText }}>Customer Name *</label>
              <input
                type="text"
                placeholder="e.g. ADNOC HQ"
                value={formData.customer_name}
                onChange={e => setFormData(p => ({ ...p, customer_name: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: mutedText }}>Owner / Lead</label>
              <input
                type="text"
                placeholder="e.g. Ahmed Al-Rashidi"
                value={formData.owner_name}
                onChange={e => setFormData(p => ({ ...p, owner_name: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: mutedText }}>Estimated Value</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="0"
                  value={formData.value_amount}
                  onChange={e => setFormData(p => ({ ...p, value_amount: e.target.value }))}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <select
                  value={formData.value_currency}
                  onChange={e => setFormData(p => ({ ...p, value_currency: e.target.value }))}
                  style={{ ...inputStyle, width: 80 }}
                >
                  <option>OMR</option>
                  <option>USD</option>
                  <option>AED</option>
                  <option>SAR</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: mutedText }}>Deadline</label>
              <input
                type="date"
                value={formData.deadline_at}
                onChange={e => setFormData(p => ({ ...p, deadline_at: e.target.value }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: mutedText }}>Priority</label>
              <select value={formData.priority} onChange={e => setFormData(p => ({ ...p, priority: e.target.value }))} style={inputStyle}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-600 mb-1.5 block" style={{ color: mutedText }}>Initial Stage</label>
              <select value={formData.stage} onChange={e => setFormData(p => ({ ...p, stage: e.target.value }))} style={inputStyle}>
                {['Initiated', 'Req. Received', 'Brief Done', 'Team Assigned'].map(s => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-600 mb-1.5 block" style={{ color: mutedText }}>Customer Requirement (optional)</label>
              <textarea
                rows={3}
                placeholder="Describe the customer's requirement..."
                value={formData.requirement_text}
                onChange={e => setFormData(p => ({ ...p, requirement_text: e.target.value }))}
                style={{ ...inputStyle, resize: 'vertical' as const }}
              />
            </div>
          </div>
          {saveError && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(255,71,87,0.1)', color: '#FF4757', border: '1px solid rgba(255,71,87,0.2)' }}>{saveError}</p>
          )}
          <div className="flex gap-3 pt-1">
            <button onClick={handleCreateProposal} disabled={saving} className="btn-primary text-sm px-5 py-2.5 flex items-center gap-2">
              <Icon name="PlusIcon" size={15} />{saving ? 'Creating...' : 'Create Proposal'}
            </button>
            <button onClick={() => setShowNewModal(false)} className="btn-secondary text-sm px-5 py-2.5">Cancel</button>
          </div>
        </div>
      </Modal>
    </AppLayout>
  );
}
